import axios from 'axios';
import {
    FORM_LIST_REQUEST,
    FORM_LIST_SUCCESS,
    FORM_LIST_FAIL,
    FORM_DETAILS_REQUEST,
    FORM_DETAILS_SUCCESS,
    FORM_DETAILS_FAIL,
    FORM_CREATE_REQUEST,
    FORM_CREATE_SUCCESS,
    FORM_CREATE_FAIL,
    FORM_UPDATE_REQUEST,
    FORM_UPDATE_SUCCESS,
    FORM_UPDATE_FAIL,
    FORM_DELETE_REQUEST,
    FORM_DELETE_SUCCESS,
    FORM_DELETE_FAIL,
    FORM_DTO_REQUEST,
    FORM_DTO_SUCCESS,
    FORM_DTO_FAIL,
    FORM_LIST_BYIDS_REQUEST,
    FORM_LIST_BYIDS_SUCCESS,
    FORM_LIST_BYIDS_FAIL
} from '../constants/formConstants';

const getAuthConfig = (token) => ({
    headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
});

const formatErrorMessage = (error) => {
    const errorMessages = [];
    if (error.response?.data) {
        if (error.response.data.errors) {
            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                errorMessages.push(`${key}: ${value[0]}`);
            });
        }
        if (error.response.data.detail) {
            errorMessages.push(`Detail: ${error.response.data.detail}`);
        }
        if (error.response.data.message) {
            errorMessages.push(`Message: ${error.response.data.message}`);
        }
    }
    return errorMessages.length > 0 ? errorMessages : [error.message || "An error occurred, please try again later"];
};

export const listForms = (filter) => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_LIST_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        // If entityType is provided, use the entity-type endpoint
        if (filter?.entityType) {
            const { data } = await axios.get(
                `/dotnet_api/v1/Form/entity-type/${filter.entityType}`,
                getAuthConfig(userInfo.token)
            );
            // Entity-type endpoint returns array directly, wrap it to match reducer format
            dispatch({ 
                type: FORM_LIST_SUCCESS, 
                payload: { 
                    items: Array.isArray(data) ? data : [],
                    totalPages: 1,
                    currentPage: 1,
                    totalCount: Array.isArray(data) ? data.length : 0
                } 
            });
            return;
        }
        
        const config = {
            ...getAuthConfig(userInfo.token),
            params: filter || {}
        };
        
        const { data } = await axios.get('/dotnet_api/v1/Form/all', config);
        dispatch({ type: FORM_LIST_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: FORM_LIST_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const getFormDetails = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_DETAILS_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.get(
            `/dotnet_api/v1/Form/${id}`,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: FORM_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: FORM_DETAILS_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};


export const createForm = (changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_CREATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.post(
            '/dotnet_api/v1/Form',
            changes,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: FORM_CREATE_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: FORM_CREATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const updateForm = (id, changes) => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_UPDATE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.patch(
            `/dotnet_api/v1/Form/${id}`,
            changes,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: FORM_UPDATE_SUCCESS, payload: data });
        dispatch({ type: FORM_DETAILS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: FORM_UPDATE_FAIL,
            payload: formatErrorMessage(error)
        });
    }
};

export const deleteForm = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_DELETE_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        await axios.delete(
            `/dotnet_api/v1/Form/${id}`,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: FORM_DELETE_SUCCESS });
    } catch (error) {
        dispatch({
            type: FORM_DELETE_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const loadFormDto = () => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_DTO_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        const { data } = await axios.get(
            '/dotnet_api/v1/Form/dto',
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: FORM_DTO_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: FORM_DTO_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const listFormsByIds = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: FORM_LIST_BYIDS_REQUEST });
        const { userLogin: { userInfo } } = getState();
        
        if (!ids || ids.length === 0) {
            dispatch({
                type: FORM_LIST_BYIDS_SUCCESS,
                payload: []
            });
            return;
        }
        
        const { data } = await axios.post(
            '/dotnet_api/v1/Form/ids/all',
            ids,
            getAuthConfig(userInfo.token)
        );
        
        dispatch({ type: FORM_LIST_BYIDS_SUCCESS, payload: data });
    } catch (error) {
        dispatch({
            type: FORM_LIST_BYIDS_FAIL,
            payload: formatErrorMessage(error)[0]
        });
    }
};

export const exportFormsJson = () => async (dispatch, getState) => {
    const { userLogin: { userInfo } } = getState();
    const { data } = await axios.get(
        '/dotnet_api/v1/Form/export',
        getAuthConfig(userInfo.token)
    );
    return data;
};

export const fetchSeedDefaults = () => async (dispatch, getState) => {
    const { userLogin: { userInfo } } = getState();
    const { data } = await axios.get(
        '/dotnet_api/v1/Form/seed/defaults',
        getAuthConfig(userInfo.token)
    );
    return data;
};

export const previewFormsImport = (forms) => async (dispatch, getState) => {
    const { userLogin: { userInfo } } = getState();
    const { data } = await axios.post(
        '/dotnet_api/v1/Form/import/preview',
        { forms },
        getAuthConfig(userInfo.token)
    );
    return data;
};

export const applyFormsImport = (items) => async (dispatch, getState) => {
    const { userLogin: { userInfo } } = getState();
    const { data } = await axios.post(
        '/dotnet_api/v1/Form/import/apply',
        { items },
        getAuthConfig(userInfo.token)
    );
    return data;
};

